import { useMemo } from 'react';

import { type EventRegistrationHouseholdMember } from '~/domain/households/queries/get-household-members-for-event-registration.server';

interface UseHouseholdWaiverContextParams {
  householdMembers?: EventRegistrationHouseholdMember[];
  selectedMemberIds: string[];
  authenticatedMemberId?: string | null;
}

/**
 * Hook to manage household context for waiver forms.
 * Provides computed values for determining which household members need waiver signatures.
 */
export function useHouseholdWaiverContext({
  householdMembers,
  selectedMemberIds,
  authenticatedMemberId,
}: UseHouseholdWaiverContextParams) {
  // Determine if we're in household context
  const hasHouseholdContext = Boolean(
    householdMembers && selectedMemberIds.length > 0 && authenticatedMemberId,
  );

  // Filter selected members to get children only
  const selectedChildren = useMemo(() => {
    if (!householdMembers || !hasHouseholdContext) return [];
    return householdMembers.filter(
      (member) => member.memberType === 'child' && selectedMemberIds.includes(member.id),
    );
  }, [householdMembers, selectedMemberIds, hasHouseholdContext]);

  // Get authenticated adult member
  const authenticatedAdult = useMemo(() => {
    if (!householdMembers || !authenticatedMemberId) return null;
    return householdMembers.find((member) => member.id === authenticatedMemberId) || null;
  }, [householdMembers, authenticatedMemberId]);

  // Check if authenticated adult is selected
  const isAuthenticatedAdultSelected = useMemo(() => {
    return authenticatedMemberId ? selectedMemberIds.includes(authenticatedMemberId) : false;
  }, [selectedMemberIds, authenticatedMemberId]);

  // Check if any adult members (other than authenticated) are selected
  const hasOtherSelectedAdults = useMemo(() => {
    if (!householdMembers || !hasHouseholdContext) return false;
    return householdMembers.some(
      (member) =>
        member.memberType === 'adult' &&
        member.id !== authenticatedMemberId &&
        selectedMemberIds.includes(member.id),
    );
  }, [householdMembers, selectedMemberIds, authenticatedMemberId, hasHouseholdContext]);

  const selectedChildIds = useMemo(() => {
    return selectedChildren.map((c) => c.id);
  }, [selectedChildren]);

  return {
    hasHouseholdContext,
    selectedChildren,
    authenticatedAdult,
    isAuthenticatedAdultSelected,
    hasOtherSelectedAdults,
    selectedChildIds,
  };
}
